import { useDispatch } from 'react-redux';
import { useLocalization } from '@/Hooks';
import { useMemo } from 'react';
import { TourType } from '@/Components/Tour/Tour';
import { modalWindowsActions } from '@/ModalWindows/services';
import { ModalType } from '@/Enums';
import { salesPageActions } from '@/Pages/Sales/services';
import { OrderNoteType } from '@/Enums/OrderNoteType';
import { emailSendModalActions, SendEmailStep } from '@/ModalWindows/EmailSendModal/services/reducer';
import { shippingLabelModalActions } from '@/ModalWindows/ShippingLabelModal/services';
import { ShippingLabelStep } from '@/ModalWindows/ShippingLabelModal/Types';
import { splitOrdersModalActions } from '@/ModalWindows/SplitOrdersModal/services/reducer';
import { documentPrintModalActions, DocumentPrintStep } from '@/ModalWindows/DocumentPrintModal/services/reducer';

export function useModals() {
  const dispatch = useDispatch();

  const {
    tours: {
      Sales: { OrderModalTour, CopyOrderModalTour, NotesModalTour, HistoryModalTour },
      Modals: {
        DocumentPrintTour,
        DocumentCreatingTour,
        ChangePaymentStatusTour,
        ChangeDeliveryStatusTour,
        ChangeStatusTour,
        EmailSendTour,
        SplitOrdersTour,
        MergeOrdersTour,
        ShippingLabelTour,
      },
    },
  } = useLocalization();

  const orderModalTour = useMemo<TourType>(
    () => ({
      id: 'orderModalTour',
      title: OrderModalTour.Title,
      before: () => {
        dispatch(salesPageActions.setTabActiveStep({ key: 'orderModalStep', value: 0 }));
        dispatch(salesPageActions.showOrderModal({ modalType: 'create' }));
        dispatch(salesPageActions.setOrderProductModal({ visible: false }));
      },
      after: () => {
        dispatch(salesPageActions.closeOrderModal());
      },
      steps: [
        {
          content: OrderModalTour.Step1.Text,
          placement: 'top',
          target: '#mainOrderInfo',
          title: OrderModalTour.Step1.Title,
          disableBeacon: true,
          link: OrderModalTour.Step1.DetailsLink,
          callback: () => {
            dispatch(salesPageActions.setTabActiveStep({ key: 'orderModalStep', value: 0 }));
          },
        },
        {
          content: OrderModalTour.Step2.Text,
          placement: 'left',
          target: '#orderItemsInfo',
          title: OrderModalTour.Step2.Title,
          disableBeacon: true,
          link: OrderModalTour.Step2.DetailsLink,
          callback: () => {
            dispatch(salesPageActions.setTabActiveStep({ key: 'orderModalStep', value: 1 }));
            dispatch(salesPageActions.setOrderProductModal({ visible: false }));
          },
        },
        {
          content: OrderModalTour.Step3.Text,
          placement: 'left',
          target: '#selectProductModal',
          title: OrderModalTour.Step3.Title,
          disableBeacon: true,
          link: OrderModalTour.Step3.DetailsLink,
          callback: () => {
            dispatch(salesPageActions.setTabActiveStep({ key: 'orderModalStep', value: 1 }));
            dispatch(salesPageActions.setOrderProductModal({ visible: true }));
          },
        },
        {
          content: OrderModalTour.Step4.Text,
          placement: 'left',
          target: '#orderPreview',
          title: OrderModalTour.Step4.Title,
          disableBeacon: true,
          link: OrderModalTour.Step4.DetailsLink,
          callback: () => {
            dispatch(salesPageActions.setTabActiveStep({ key: 'orderModalStep', value: 2 }));
          },
        },
      ],
    }),
    [OrderModalTour],
  );

  const copyOrderModalTour = useMemo<TourType>(
    () => ({
      id: 'copyOrderModalTour',
      title: CopyOrderModalTour.Title,
      before: () => {
        dispatch(salesPageActions.setCopyOrderModal(true));
      },
      after: () => {
        dispatch(salesPageActions.setCopyOrderModal(false));
      },
      steps: [
        {
          content: CopyOrderModalTour.Step1.Text,
          placement: 'auto',
          target: '#copyOrderModal',
          title: CopyOrderModalTour.Step1.Title,
          disableBeacon: true,
          link: CopyOrderModalTour.Step1.DetailsLink,
        },
      ],
    }),
    [CopyOrderModalTour],
  );

  const notesModalTour = useMemo<TourType>(
    () => ({
      id: 'notesModalTour',
      title: NotesModalTour.Title,
      before: () => {
        dispatch(
          salesPageActions.setNoteModalData({
            visible: true,
            data: { saleId: 1, noteType: OrderNoteType.InternalNote, noteValue: '' },
          }),
        );
      },
      after: () => {
        dispatch(salesPageActions.setNoteModalData({ visible: false }));
      },
      steps: [
        {
          content: NotesModalTour.Step1.Text,
          placement: 'auto',
          target: '#orderNote',
          title: NotesModalTour.Step1.Title,
          disableBeacon: true,
          link: NotesModalTour.Step1.DetailsLink,
        },
      ],
    }),
    [NotesModalTour],
  );

  const historyModalTour = useMemo<TourType>(
    () => ({
      id: 'historyModalTour',
      title: HistoryModalTour.Title,
      before: () => {
        dispatch(salesPageActions.setHistoryModalData({ visible: true }));
      },
      after: () => {
        dispatch(salesPageActions.setHistoryModalData({ visible: false }));
      },
      steps: [
        {
          content: HistoryModalTour.Step1.Text,
          placement: 'auto',
          target: '#historyListModal',
          title: HistoryModalTour.Step1.Title,
          disableBeacon: true,
          link: HistoryModalTour.Step1.DetailsLink,
        },
      ],
    }),
    [HistoryModalTour],
  );

  const documentCreatingTour = useMemo<TourType>(
    () => ({
      id: 'documentCreatingTour',
      title: DocumentCreatingTour.Title,
      before: () => {
        dispatch(modalWindowsActions.openModal({ modalType: ModalType.GenerateDocumentNumberModal }));
      },
      after: () => {
        dispatch(modalWindowsActions.openModal({ modalType: ModalType.None }));
      },
      steps: [
        {
          content: DocumentCreatingTour.Step1.Text,
          placement: 'auto',
          target: '#generateNumberModal',
          title: DocumentCreatingTour.Step1.Title,
          disableBeacon: true,
          link: DocumentCreatingTour.Step1.DetailsLink,
        },
      ],
    }),
    [DocumentCreatingTour],
  );

  const changePaymentStatusTour = useMemo<TourType>(
    () => ({
      id: 'changePaymentStatusTour',
      title: ChangePaymentStatusTour.Title,
      before: () => {
        dispatch(modalWindowsActions.openModal({ modalType: ModalType.ChangePaymentStatus }));
      },
      after: () => {
        dispatch(modalWindowsActions.openModal({ modalType: ModalType.None }));
      },
      steps: [
        {
          content: ChangePaymentStatusTour.Step1.Text,
          placement: 'auto',
          target: '#changeStatus',
          title: ChangePaymentStatusTour.Step1.Title,
          disableBeacon: true,
          link: ChangePaymentStatusTour.Step1.DetailsLink,
        },
      ],
    }),
    [ChangePaymentStatusTour],
  );

  const changeDeliveryStatusTour = useMemo<TourType>(
    () => ({
      id: 'changeDeliveryStatusTour',
      title: ChangeDeliveryStatusTour.Title,
      before: () => {
        dispatch(modalWindowsActions.openModal({ modalType: ModalType.ChangeDeliveryStatus }));
      },
      after: () => {
        dispatch(modalWindowsActions.openModal({ modalType: ModalType.None }));
      },
      steps: [
        {
          content: ChangeDeliveryStatusTour.Step1.Text,
          placement: 'auto',
          target: '#changeStatus',
          title: ChangeDeliveryStatusTour.Step1.Title,
          disableBeacon: true,
          link: ChangeDeliveryStatusTour.Step1.DetailsLink,
        },
      ],
    }),
    [ChangeDeliveryStatusTour],
  );

  const documentPrintTour = useMemo<TourType>(
    () => ({
      id: 'documentPrintTour',
      title: DocumentPrintTour.Title,
      before: () => {
        dispatch(modalWindowsActions.openModal({ modalType: ModalType.DocumentTemplates }));
      },
      after: () => {
        dispatch(modalWindowsActions.openModal({ modalType: ModalType.None }));
      },
      steps: [
        {
          content: DocumentPrintTour.Step1.Text,
          placement: 'auto',
          target: '#documentPrintModal',
          title: DocumentPrintTour.Step1.Title,
          disableBeacon: true,
          link: DocumentPrintTour.Step1.DetailsLink,
          callback: () => {
            dispatch(documentPrintModalActions.setDocumentPrintStep(DocumentPrintStep.SelectTemplate));
          },
        },
        {
          content: DocumentPrintTour.Step2.Text,
          placement: 'auto',
          target: '#selectDocumentModal',
          title: DocumentPrintTour.Step2.Title,
          disableBeacon: true,
          link: DocumentPrintTour.Step2.DetailsLink,
          callback: () => {
            dispatch(documentPrintModalActions.setDocumentPrintStep(DocumentPrintStep.SelectDocumentNumber));
          },
        },
      ],
    }),
    [DocumentPrintTour],
  );

  const changeStatusTour = useMemo<TourType>(
    () => ({
      id: 'changeStatusTour',
      title: ChangeStatusTour.Title,
      before: () => {
        dispatch(modalWindowsActions.openModal({ modalType: ModalType.ChangeOtherStatuses }));
      },
      after: () => {
        dispatch(modalWindowsActions.openModal({ modalType: ModalType.None }));
      },
      steps: [
        {
          content: ChangeStatusTour.Step1.Text,
          placement: 'auto',
          target: '#changeStatus',
          title: ChangeStatusTour.Step1.Title,
          disableBeacon: true,
          link: ChangeStatusTour.Step1.DetailsLink,
        },
      ],
    }),
    [ChangeStatusTour],
  );

  const emailSendTour = useMemo<TourType>(
    () => ({
      id: 'emailSendTour',
      title: EmailSendTour.Title,
      before: () => {
        dispatch(modalWindowsActions.openModal({ modalType: ModalType.EmailSend }));
      },
      after: () => {
        dispatch(modalWindowsActions.openModal({ modalType: ModalType.None }));
      },
      steps: [
        {
          content: EmailSendTour.Step1.Text,
          placement: 'auto',
          target: '#selectTemplateModal',
          title: EmailSendTour.Step1.Title,
          disableBeacon: true,
          link: EmailSendTour.Step1.DetailsLink,
          callback: () => {
            dispatch(emailSendModalActions.setSendEmailStep(SendEmailStep.SelectTemplate));
          },
        },
        {
          content: EmailSendTour.Step2.Text,
          placement: 'auto',
          target: '#selectDocumentModal',
          title: EmailSendTour.Step2.Title,
          disableBeacon: true,
          link: EmailSendTour.Step2.DetailsLink,
          callback: () => {
            dispatch(emailSendModalActions.setSendEmailStep(SendEmailStep.SelectDocumentNumber));
          },
        },
        {
          content: EmailSendTour.Step3.Text,
          placement: 'auto',
          target: '#emailPreviewModal',
          title: EmailSendTour.Step3.Title,
          disableBeacon: true,
          link: EmailSendTour.Step3.DetailsLink,
          callback: () => {
            dispatch(emailSendModalActions.setSendEmailStep(SendEmailStep.EmailPreview));
          },
        },
      ],
    }),
    [EmailSendTour],
  );

  const splitOrdersTour = useMemo<TourType>(
    () => ({
      id: 'splitOrdersTour',
      title: SplitOrdersTour.Title,
      before: () => {
        dispatch(splitOrdersModalActions.setInitialNewOrdersCount(2));
        dispatch(modalWindowsActions.openModal({ modalType: ModalType.SplitOrders }));
      },
      after: () => {
        dispatch(modalWindowsActions.openModal({ modalType: ModalType.None }));
      },
      steps: [
        {
          content: SplitOrdersTour.Step1.Text,
          placement: 'auto',
          target: '#splitOrdersModal',
          title: SplitOrdersTour.Step1.Title,
          disableBeacon: true,
          link: SplitOrdersTour.Step1.DetailsLink,
        },
      ],
    }),
    [SplitOrdersTour],
  );

  const mergeOrdersTour = useMemo<TourType>(
    () => ({
      id: 'mergeOrdersTour',
      title: MergeOrdersTour.Title,
      before: () => {
        dispatch(modalWindowsActions.openModal({ modalType: ModalType.MergeOrders }));
      },
      after: () => {
        dispatch(modalWindowsActions.openModal({ modalType: ModalType.None }));
      },
      steps: [
        {
          content: MergeOrdersTour.Step1.Text,
          placement: 'auto',
          target: '#mergeOrdersModal',
          title: MergeOrdersTour.Step1.Title,
          disableBeacon: true,
          link: MergeOrdersTour.Step1.DetailsLink,
        },
      ],
    }),
    [MergeOrdersTour],
  );

  const shippingLabelTour = useMemo<TourType>(
    () => ({
      id: 'shippingLabelTour',
      title: ShippingLabelTour.Title,
      before: () => {
        dispatch(modalWindowsActions.openModal({ modalType: ModalType.ShippingLabel }));
      },
      after: () => {
        dispatch(modalWindowsActions.openModal({ modalType: ModalType.None }));
      },
      steps: [
        {
          content: ShippingLabelTour.Step1.Text,
          placement: 'auto',
          target: '#addressValidationModal',
          title: ShippingLabelTour.Step1.Title,
          disableBeacon: true,
          link: ShippingLabelTour.Step1.DetailsLink,
          callback: () => {
            dispatch(shippingLabelModalActions.setShippingLabelStep(ShippingLabelStep.Validation));
          },
        },
        {
          content: ShippingLabelTour.Step2.Text,
          placement: 'auto',
          target: '#orderAutomatizationTable',
          title: ShippingLabelTour.Step2.Title,
          disableBeacon: true,
          link: ShippingLabelTour.Step2.DetailsLink,
          callback: () => {
            dispatch(shippingLabelModalActions.setShippingLabelStep(ShippingLabelStep.OrderWithoutAutomatizaion));
          },
        },
      ],
    }),
    [ShippingLabelTour],
  );

  return [
    orderModalTour,
    copyOrderModalTour,
    notesModalTour,
    historyModalTour,
    documentCreatingTour,
    changePaymentStatusTour,
    changeDeliveryStatusTour,
    documentPrintTour,
    changeStatusTour,
    emailSendTour,
    splitOrdersTour,
    mergeOrdersTour,
    shippingLabelTour,
  ];
}
